import { useState, useEffect, useRef } from 'react';
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

/**
 * Find the Y% position of a line item on a PDF page by searching the text layer.
 */
function findItemOnPages(pagesText, item) {
  if (!item) return null;

  const desc = (item.description || '').trim().toLowerCase();
  if (!desc) return null;

  for (const pageData of pagesText) {
    const rowMap = {};
    for (const ti of pageData.items) {
      if (!ti.str || !ti.str.trim()) continue;
      const yKey = Math.round(ti.transform[5] * 2) / 2;
      if (!rowMap[yKey]) rowMap[yKey] = { y: ti.transform[5], fontSize: Math.abs(ti.transform[0]), texts: [] };
      rowMap[yKey].texts.push(ti.str);
      const fs = Math.abs(ti.transform[0]);
      if (fs > rowMap[yKey].fontSize) rowMap[yKey].fontSize = fs;
    }

    const prefixes = [
      desc,
      desc.substring(0, 20),
      desc.substring(0, 12),
    ].filter(p => p.length >= 4);

    for (const prefix of prefixes) {
      for (const row of Object.values(rowMap)) {
        const rowText = row.texts.join(' ').toLowerCase();
        if (rowText.includes(prefix)) {
          const yPercent = ((pageData.pageHeight - row.y) / pageData.pageHeight) * 100;
          const fontPct = (row.fontSize / pageData.pageHeight) * 100;
          return { page: pageData.pageNum, y: yPercent, fontSize: fontPct };
        }
      }
    }
  }

  return null;
}

export default function SourceViewerModal({ pdfUrl, item, allItems = [], onClose, onItemChange }) {
  const [pageImages, setPageImages] = useState([]);
  const [pagesText, setPagesText] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bbox, setBbox] = useState(null);
  const [imgHeights, setImgHeights] = useState({});
  const scrollRef = useRef(null);
  const imgRefs = useRef({});

  const currentIndex = allItems.findIndex(i => i.id === item.id);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < allItems.length - 1;

  // Build header subtitle
  const subtitle = [
    item.invoices?.invoice_number ? `#${item.invoices.invoice_number}` : null,
    item.vendor_name,
    item.bill_date,
  ].filter(Boolean).join(' \u00b7 ');

  // Render PDF pages
  useEffect(() => {
    if (!pdfUrl) return;
    let cancelled = false;

    async function renderPages() {
      setLoading(true);
      setError(null);
      try {
        const pdf = await pdfjsLib.getDocument(pdfUrl).promise;
        const images = [];
        const textData = [];

        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const scale = 2;
          const viewport = page.getViewport({ scale });
          const baseViewport = page.getViewport({ scale: 1 });

          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          const ctx = canvas.getContext('2d');
          await page.render({ canvasContext: ctx, viewport }).promise;

          const textContent = await page.getTextContent();

          images.push({ pageNum: i, dataUrl: canvas.toDataURL('image/png') });
          textData.push({
            pageNum: i,
            pageHeight: baseViewport.height,
            items: textContent.items,
          });
        }

        if (!cancelled) {
          setPageImages(images);
          setPagesText(textData);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('PDF render error:', err);
          setError('Failed to render PDF');
          setLoading(false);
        }
      }
    }

    renderPages();
    return () => { cancelled = true; };
  }, [pdfUrl]);

  // Find star position and scroll to it
  useEffect(() => {
    if (pagesText.length === 0 || !item) {
      setBbox(null);
      return;
    }
    const found = findItemOnPages(pagesText, item);
    setBbox(found || null);

    // Scroll to the page with the found item
    if (found && scrollRef.current) {
      const targetImg = imgRefs.current[found.page];
      if (targetImg) {
        setTimeout(() => {
          targetImg.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
    }
  }, [item, pagesText]);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const onImgLoad = (pageNum) => {
    const el = imgRefs.current[pageNum];
    if (el) setImgHeights(prev => ({ ...prev, [pageNum]: el.clientHeight }));
  };

  useEffect(() => {
    const handleResize = () => {
      const updated = {};
      for (const [num, el] of Object.entries(imgRefs.current)) {
        if (el) updated[num] = el.clientHeight;
      }
      setImgHeights(updated);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const hasBoundingBox = bbox && bbox.y !== undefined;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />

      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-4xl mx-4 max-h-[95vh] flex flex-col overflow-hidden">
        <style>{`@keyframes starOpacityFlash { 0%,100% { opacity: 1; } 50% { opacity: 0.15; } }`}</style>

        {/* Header — minimal */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-200 bg-white shrink-0">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-zinc-900 truncate">
              {item.description || 'Source Document'}
            </h2>
            {subtitle && (
              <p className="text-xs text-zinc-400 truncate mt-0.5">{subtitle}</p>
            )}
          </div>
          <button onClick={onClose} className="ml-4 p-1.5 rounded-lg hover:bg-zinc-100 text-zinc-400 hover:text-zinc-600 transition-colors shrink-0">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable PDF body — continuous scroll, all pages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto bg-zinc-100">
          {loading ? (
            <div className="flex items-center justify-center h-64 text-zinc-400">
              <div className="text-center">
                <div className="w-6 h-6 border-2 border-zinc-300 border-t-zinc-600 rounded-full animate-spin mx-auto mb-2" />
                <p className="text-xs">Rendering PDF...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64 text-red-500 text-sm">{error}</div>
          ) : pageImages.length === 0 ? (
            <div className="flex items-center justify-center h-64 text-zinc-400 text-sm">No pages available</div>
          ) : (
            <div className="py-4 space-y-2">
              {pageImages.map((img) => (
                <div key={img.pageNum} className="relative mx-auto" style={{ maxWidth: '100%' }}>
                  <img
                    ref={(el) => { imgRefs.current[img.pageNum] = el; }}
                    src={img.dataUrl}
                    alt={`Page ${img.pageNum}`}
                    className="w-full h-auto"
                    onLoad={() => onImgLoad(img.pageNum)}
                  />

                  {/* Star indicator */}
                  {hasBoundingBox && bbox.page === img.pageNum && (imgHeights[img.pageNum] || 0) > 0 && (
                    <div
                      className="absolute pointer-events-none"
                      style={{
                        left: '1%',
                        top: `${bbox.y}%`,
                        transform: 'translateY(-50%)',
                        animation: 'starOpacityFlash 1.5s ease-in-out infinite',
                        lineHeight: 1,
                      }}
                    >
                      <span style={{
                        fontSize: `${Math.round(imgHeights[img.pageNum] * bbox.fontSize / 100)}px`,
                        color: '#000',
                        fontWeight: 'bold',
                        display: 'block',
                      }}>&#x2733;</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer — slim status bar */}
        <div className="flex items-center justify-between px-5 py-2 border-t border-zinc-200 bg-white shrink-0">
          {/* Line item navigation (when viewing from a table with multiple items) */}
          {allItems.length > 1 ? (
            <div className="flex items-center gap-2">
              <button
                onClick={() => hasPrev && onItemChange(allItems[currentIndex - 1])}
                disabled={!hasPrev}
                className="text-xs text-zinc-500 hover:text-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                &larr; Prev
              </button>
              <span className="text-xs text-zinc-400">Line {item.line_number}/{allItems.length}</span>
              <button
                onClick={() => hasNext && onItemChange(allItems[currentIndex + 1])}
                disabled={!hasNext}
                className="text-xs text-zinc-500 hover:text-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Next &rarr;
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1 text-xs text-zinc-400">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126z" />
              </svg>
              View-only mode &ndash; scrolling enabled
            </div>
          )}

          <span className="text-xs text-zinc-400">Total pages: {pageImages.length}</span>
        </div>
      </div>
    </div>
  );
}
